import { ROLE_HIERARCHY, type UserRole } from '@utazzo/shared';
import {
  AudioLines,
  BedDouble,
  Bell,
  Fingerprint,
  HeartPulse,
  Briefcase,
  CheckSquare,
  ClipboardCheck,
  Building2,
  CalendarClock,
  CalendarRange,
  Car,
  ChartColumn,
  ClipboardList,
  Clock as ClockIcon,
  Clock3,
  Coins,
  Compass,
  Contact,
  FileSpreadsheet,
  FileText,
  Flag,
  FileMinus,
  GitBranch,
  Boxes,
  Globe,
  HandCoins,
  Hotel as HotelIcon,
  Inbox,
  KeyRound,
  Landmark,
  FileBadge,
  Languages as LanguagesIcon,
  LayoutDashboard,
  PartyPopper,
  LayoutGrid,
  ListChecks,
  ListTree,
  type LucideIcon,
  MapPin,
  MapPinned,
  Menu as MenuIcon,
  Megaphone,
  MessageSquare,
  Package,
  Palette,
  Percent,
  Phone,
  Plane,
  PlaneTakeoff,
  ReceiptIndianRupee,
  Route,
  Rss,
  Salad,
  ScanSearch,
  MessageCircle,
  MessageSquareWarning,
  ScrollText,
  Settings,
  Share2,
  ShieldCheck,
  Ship,
  Sparkles,
  Tags,
  Target,
  ThumbsDown,
  Ticket,
  Truck,
  UserRoundX,
  Users,
  UserX,
  Utensils,
  UtensilsCrossed,
} from 'lucide-react';

import { MARKETING_IN_ROLE_CODE } from './home-path';

import type { WebsitePermission } from '@/features/website-cms/lib';

/**
 * WEBSITE_MANAGER + every role that reports into it (directly or transitively,
 * via the org-chart parent map) + ADMIN. Computed from ROLE_HIERARCHY so any
 * future website sub-role (whose parent chain reaches WEBSITE_MANAGER) inherits
 * Website Dashboard visibility automatically, with no change here.
 */
const rolesUnder = (root: UserRole): UserRole[] => {
  const out = new Set<UserRole>([root]);
  for (let changed = true; changed; ) {
    changed = false;
    for (const role of Object.keys(ROLE_HIERARCHY) as UserRole[]) {
      if (!out.has(role) && ROLE_HIERARCHY[role].some((p) => out.has(p))) {
        out.add(role);
        changed = true;
      }
    }
  }
  return [...out];
};

/** WEBSITE_MANAGER + every role under it (now WEBSITE_SEO + future sub-roles),
 *  without ADMIN - the website-only roles that are scoped to the website module
 *  and must be kept out of lead/CRM screens (nav `hideForRoles` + route deny). */
export const WEBSITE_ROLES: ReadonlyArray<UserRole> = rolesUnder('WEBSITE_MANAGER');

export const WEBSITE_DASHBOARD_ROLES: ReadonlyArray<UserRole> = ['ADMIN', ...WEBSITE_ROLES];

/**
 * Single source of truth for the admin sidebar + command-palette destinations.
 *
 * Each item is gated either by a website-CMS `WebsitePermission` (checked via
 * `useCan`) or, for the legacy admin-only Users screen, by an explicit role.
 * Items with neither gate are always visible.
 */
export interface NavItemConfig {
  /** Stable id (also the i18n key under shell.nav.*). */
  id:
    | 'welcome'
    | 'website-dashboard'
    | 'products-dashboard'
    | 'leads'
    | 'converted-leads'
    | 'fund-requests'
    | 'ops-approvals'
    | 'fixed-departures'
    | 'lead-parsing'
    | 'marketing-reports'
    | 'callsense'
    | 'lead-approvals'
    | 'exit-unassigned'
    | 'catalog-approvals'
    | 'website-approvals'
    | 'lead-imports'
    | 'scheduler'
    | 'status'
    | 'blogs'
    | 'pages'
    | 'career'
    | 'welcome-page'
    | 'media'
    | 'navigation'
    | 'destinations'
    | 'partners'
    | 'press'
    | 'customer-pdf'
    | 'cities-cms'
    | 'countries'
    | 'states'
    | 'currencies'
    | 'theme-collections'
    | 'hero-inclusions'
    | 'inclusions-exclusions'
    | 'query-types'
    | 'query-sources'
    | 'query-sub-sources'
    | 'query-mediums'
    | 'query-statuses'
    | 'query-sub-statuses'
    | 'customers'
    | 'ticketing-action'
    | 'ticketing-flights'
    | 'ticketing-hotels'
    | 'ticketing-drop-offs'
    | 'ticketing-activities'
    | 'ticketing-reports'
    | 'coupons'
    | 'discounts'
    | 'additional-discounts'
    | 'agent-targets'
    | 'lost-reasons'
    | 'marketing-cost-heads'
    | 'departments'
    | 'sub-branches'
    | 'call-statuses'
    | 'call-sub-statuses'
    | 'suppliers'
    | 'airlines'
    | 'markup-profiles'
    | 'branches'
    | 'hotels'
    | 'cruises'
    | 'activities'
    | 'meal-plans'
    | 'room-types'
    | 'occupancy-types'
    | 'vehicle-types'
    | 'languages'
    | 'cuisines'
    | 'transfers'
    | 'visas'
    | 'insurance'
    | 'meal-services'
    | 'packages'
    | 'destination-pages'
    | 'category-pages'
    | 'tourism-boards'
    | 'landing-pages'
    | 'best-time-to-visit'
    | 'best-places-to-visit'
    | 'itineraries'
    | 'itinerary-reprice-approvals'
    | 'itinerary-void-approvals'
    | 'supplier-change-approvals'
    | 'tbo'
    | 'secrets'
    | 'meta-capi'
    | 'settings'
    | 'roles'
    | 'session-audit'
    | 'tbo-audit'
    | 'whatsapp-log'
    | 'whatsapp'
    | 'invoices'
    | 'sales-register'
    | 'client-outstanding'
    | 'supplier-paid-outstanding'
    | 'balance-due'
    | 'bank-statement'
    | 'operation-pending'
    | 'deposit-booking'
    | 'monthly-sale'
    | 'supplier-payments'
    | 'agent-report-card'
    | 'bank-reconcile'
    | 'supplier-payment-schedule'
    | 'supplier-ledger'
    | 'gst-output-register'
    | 'tcs-register'
    | 'credit-notes'
    | 'destinations-master'
    | 'trip-types'
    | 'followup-reasons'
    | 'blog-categories'
    | 'notifications'
    | 'users';
  to: string;
  icon: LucideIcon;
  /** CMS permission required to access. Omit for always-visible items. */
  permission?: WebsitePermission;
  /** Hard role gate (used by the legacy admin-only Users screen). */
  requireRole?: ReadonlyArray<UserRole>;
  /**
   * Permission code from the role/permission DB (ADR-0039 Phase 3),
   * e.g. "customer.read". When set, the item is hidden if the caller's
   * effective permission set doesn't include this code. ADMIN bypasses
   * the check (admins always see everything in the UI; if they want to
   * lock themselves out, they edit their own role's grants and re-login).
   * Falls back to allow-through when the caller has no role-table row
   * yet so the legacy `@Roles` enum path keeps working.
   */
  requirePermission?: string;
  /**
   * Omit from sidebar + ⌘K navigate lists while keeping the route and
   * breadcrumb label. Used for power-user surfaces reachable by direct URL.
   */
  hiddenFromNav?: boolean;
  /**
   * Roles for which this item is hidden from the sidebar + ⌘K, even when
   * they otherwise satisfy `requirePermission`. UI-only suppression - the
   * route guard (`RequireRole` in router.tsx) is unchanged, so it is a
   * menu-visibility tweak, not an access revocation. Use when a role holds
   * a shared permission for another surface but should not see this menu.
   */
  hideForRoles?: ReadonlyArray<UserRole>;
  /**
   * Hide this item from the sidebar + ⌘K for specific user emails (case-
   * insensitive), even when their role would otherwise see it. UI-only, like
   * `hideForRoles`; pair with a route-level `denyEmails` guard to also block
   * direct-URL access. Used for one-off per-person exclusions.
   */
  hideForEmails?: ReadonlyArray<string>;
  /**
   * Hide this item for holders of specific CUSTOM role codes (from the /roles
   * master, e.g. `marketing_in`), matched against the user's assignedRoleCodes.
   * Needed because `hideForRoles` is typed to the fixed UserRole enum and cannot
   * target a custom role (whose base role is READ_ONLY). UI-only suppression, like
   * `hideForRoles`; the route guard is separate (AppShell already bounces these
   * roles off the page).
   */
  hideForRoleCodes?: ReadonlyArray<string>;
}

export interface NavGroupConfig {
  /** i18n key under shell.nav.section.*. */
  id:
    | 'overview'
    | 'operations'
    | 'ticketing'
    | 'inventory'
    | 'itinerary-masters'
    | 'crm'
    | 'global'
    | 'website'
    | 'finance'
    | 'financial-reports'
    | 'system';
  /** Parent icon shown for the whole section when the sidebar is collapsed. */
  icon: LucideIcon;
  items: readonly NavItemConfig[];
}

/**
 * 2026-06-26 ADR-0056. Sidebar gating switched from per-role hard
 * gates (`requireRole`) to module-level permission codes
 * (`requirePermission`). Every item below now reads its `.read` code
 * from the granular registry. ADMIN keeps a hard bypass in
 * `canAccess()` so admins can never lock themselves out from the UI.
 *
 * Legacy `requireRole` is still honored by `canAccess()` (for items
 * that genuinely have no permission code, like Dashboard) so this
 * change is fully backward-compatible.
 */
export const NAV_GROUPS: readonly NavGroupConfig[] = [
  {
    id: 'overview',
    icon: LayoutDashboard,
    // Finance is scoped to invoicing and lands on Invoices, so the Dashboard is
    // hidden from its sidebar (menu-only; the finance home redirect handles the
    // landing).
    items: [
      // Welcome Utazzer - company story + expectations, open to every role
      // EXCEPT the marketing-only role (marketing_in), which has no access to the
      // Welcome page at all (AppShell bounces it to the Marketing dashboard).
      {
        id: 'welcome',
        to: '/welcome',
        icon: PartyPopper,
        hideForRoleCodes: [MARKETING_IN_ROLE_CODE],
      },
      // Standalone read of the whole public site (blogs, pages, destination /
      // category pages, packages) and what is missing from it. Sits right under
      // Welcome. Visible to ADMIN + WEBSITE_MANAGER and every role under it
      // (now WEBSITE_SEO, and any future website sub-role) via
      // WEBSITE_DASHBOARD_ROLES; the aggregates it reads allow these roles.
      {
        id: 'website-dashboard',
        to: '/website-dashboard',
        icon: Globe,
        requireRole: WEBSITE_DASHBOARD_ROLES,
      },
      // Catalog health: can each activity / transfer / hotel / cruise / visa /
      // insurance actually be quoted in an itinerary. ADMIN + OPS (mirrors the
      // /dashboard/products-breakdown endpoint).
      {
        id: 'products-dashboard',
        to: '/products-dashboard',
        icon: Boxes,
        requireRole: ['ADMIN', 'OPS', 'CONTENT_EDITOR'],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Operations - the daily work surface (leads, customers, the quotes/trips
  // built off them, and the live TBO search used while building).
  // ---------------------------------------------------------------------------
  {
    id: 'operations',
    icon: Briefcase,
    items: [
      {
        // 2026-07-18. WEBSITE_MANAGER / WEBSITE_SEO can hold `ops.leads.read`
        // for the website-lead views, but the full CRM lead inbox is not their
        // surface - hide just this menu item for them. Menu-only: the route
        // guard is unchanged.
        id: 'leads',
        to: '/leads',
        icon: Inbox,
        requirePermission: 'ops.leads.read',
        hideForRoles: WEBSITE_ROLES,
      },
      {
        // 2026-10-14. Operations inbox: converted leads (contract signed,
        // documents in), dealt round-robin to ops executives, with what is
        // left to book on each. Gated on `ops.converted_leads` (granted to
        // ops / operations_lead / operations_executive by migration).
        id: 'converted-leads',
        to: '/operations/converted-leads',
        icon: ClipboardCheck,
        requirePermission: 'ops.converted_leads.read',
      },
      {
        // 2026-10-26. Admin only: supplier prices over the quote and itinerary
        // changes waiting for a decision (was a tab on Converted leads).
        id: 'ops-approvals',
        to: '/operations/approvals',
        icon: CheckSquare,
        requireRole: ['ADMIN'],
      },
      {
        // 2026-10-19. Accounts: supplier payments ops asked for while issuing.
        id: 'fund-requests',
        to: '/operations/fund-requests',
        icon: HandCoins,
        requirePermission: 'ops.fund_requests.read',
      },
      {
        // Full-page WhatsApp inbox (what the floating bubble opens).
        id: 'whatsapp',
        to: '/whatsapp',
        icon: MessageCircle,
        requirePermission: 'ops.whatsapp.read',
        hideForRoles: WEBSITE_ROLES,
      },
      {
        // Website-captured leads parsed into destination + package.
        // Gated on its own dedicated code (see `ops.lead_parsing` in
        // permissions-registry): admin-only by default, grant it to any
        // other role from the /roles matrix to reveal this item.
        id: 'lead-parsing',
        to: '/leads/parsing',
        icon: ScanSearch,
        requirePermission: 'ops.lead_parsing.read',
      },
      {
        // 2026-07-30. Marketing / attribution reporting - lead performance by
        // source, sub-source, medium and the Meta ad triplet. Own dedicated
        // code (see `ops.marketing_reports` in permissions-registry) so the
        // marketing team can get channel reporting without the agent-wise
        // Daily Tracker. Admin-only by default; grant from /roles.
        id: 'marketing-reports',
        to: '/reports/marketing',
        icon: Megaphone,
        requirePermission: 'ops.marketing_reports.read',
      },
      {
        // 2026-08-11. CallSense - AI call analytics (agent scorecards, call
        // quality, red flags) over analyzed CallerDesk recordings. Own
        // dedicated code (see `ops.callsense` in permissions-registry);
        // admin-only by default, grantable to any role from /roles.
        id: 'callsense',
        to: '/callsense',
        icon: AudioLines,
        requirePermission: 'ops.callsense.read',
      },
      {
        id: 'lead-approvals',
        to: '/leads/approvals',
        icon: ShieldCheck,
        // 2026-07-09. Sidebar visibility is now managed from the /roles
        // matrix via a dedicated code (see `ops.lead_approvals` in
        // permissions-registry). SALES_AGENT still holds `ops.leads.write`
        // to submit approval requests but does not hold this code, so
        // the reviewer inbox stays hidden from them. Admin can grant
        // this code to any role from the matrix.
        requirePermission: 'ops.lead_approvals.read',
      },
      {
        // 2026-08-08 (Task 7). Dedicated queue for a >2%-of-margin discount
        // reprice, routed through the lead reviewer hierarchy. Same read
        // permission as the itineraries module: ADMIN / SALES_LEAD see it as
        // reviewers, SALES_AGENT sees their own submissions read-only - the
        // server scopes rows, this only gates sidebar visibility. Sits right
        // below Lead approvals - the other "someone has to review this" queue.
        id: 'itinerary-reprice-approvals',
        to: '/itineraries/reprice-approvals',
        icon: ShieldCheck,
        requirePermission: 'ops.itineraries.read',
      },
      {
        // Void-a-won-deal requests - reopen a won deal for an amendment.
        // Same reviewer hierarchy + scoping as reprice approvals; the server
        // scopes rows, this only gates sidebar visibility.
        id: 'itinerary-void-approvals',
        to: '/itineraries/void-approvals',
        icon: ShieldCheck,
        requirePermission: 'ops.itineraries.read',
      },
      {
        // 2026-07-26. Queue of leads released from departed agents.
        // Gated on its own dedicated code (see `ops.exit_unassigned` in
        // permissions-registry): sales_lead + admin by default. The page
        // itself just drives the `?exitUnassigned=true` mode of the lead
        // list; server-side RBAC scopes rows to the caller's reports.
        id: 'exit-unassigned',
        to: '/leads/exit-unassigned',
        icon: UserX,
        requirePermission: 'ops.exit_unassigned.read',
      },
      {
        id: 'catalog-approvals',
        to: '/catalog/approvals',
        icon: ShieldCheck,
        requirePermission: 'sys.roles.read',
      },
      {
        // Website content sent up by a WEBSITE_SEO editor. Gated on the
        // destination-page read code so it lands for the website roles and
        // stays out of the sales sidebar.
        id: 'website-approvals',
        to: '/website/approvals',
        icon: ShieldCheck,
        requirePermission: 'web.destination_pages.read',
      },
      {
        id: 'lead-imports',
        to: '/leads/imports',
        icon: FileSpreadsheet,
        requirePermission: 'ops.lead_imports.read',
      },
      {
        id: 'scheduler',
        to: '/scheduler',
        icon: ClockIcon,
        requirePermission: 'ops.scheduler.read',
        hiddenFromNav: true,
      },
      {
        id: 'status',
        to: '/status',
        icon: HeartPulse,
        requirePermission: 'ops.status.read',
      },
      { id: 'customers', to: '/customers', icon: Contact, requirePermission: 'ops.customers.read' },
      {
        id: 'itineraries',
        to: '/itineraries',
        icon: Route,
        requirePermission: 'ops.itineraries.read',
      },
      {
        id: 'fixed-departures',
        to: '/fixed-departures',
        icon: CalendarRange,
        // The template master is a management surface (create / edit / delete
        // templates), so it's gated on WRITE, not read: admin holds it by
        // default and anyone granted ops.fixed_departures.write sees it, while
        // read-only holders (who only need the per-lead picker) don't get a
        // management menu they can't use.
        requirePermission: 'ops.fixed_departures.write',
      },
      // { id: 'tbo', to: '/tbo', icon: Plane, requirePermission: 'ops.tbo.read' },
    ],
  },

  // ---------------------------------------------------------------------------
  // Ticketing - website flight + hotel bookings. ADMIN by role, not by
  // permission code, on every item: the pages show passport numbers and
  // their actions move guest money. The routes carry the same guard.
  // ---------------------------------------------------------------------------
  {
    id: 'ticketing',
    icon: Ticket,
    items: [
      { id: 'ticketing-action', to: '/ticketing/action', icon: Clock3, requireRole: ['ADMIN'] },
      { id: 'ticketing-flights', to: '/ticketing/flights', icon: Plane, requireRole: ['ADMIN'] },
      { id: 'ticketing-hotels', to: '/ticketing/hotels', icon: BedDouble, requireRole: ['ADMIN'] },
      {
        id: 'ticketing-drop-offs',
        to: '/ticketing/drop-offs',
        icon: UserRoundX,
        requireRole: ['ADMIN'],
      },
      {
        id: 'ticketing-activities',
        to: '/ticketing/activities',
        icon: Compass,
        requireRole: ['ADMIN'],
      },
      {
        id: 'ticketing-reports',
        to: '/ticketing/reports',
        icon: ChartColumn,
        requireRole: ['ADMIN'],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Inventory & supply - the bookable catalog itineraries are built from.
  // ---------------------------------------------------------------------------
  {
    id: 'inventory',
    icon: Package,
    items: [
      { id: 'hotels', to: '/hotels', icon: HotelIcon, requirePermission: 'inv.hotels.read' },
      {
        id: 'cruises',
        to: '/cruises',
        icon: Ship,
        // Cruises v1 reuses the hotels read/write permission (modeled as a
        // "stay" alongside hotels); no dedicated inv.cruises.* key is seeded.
        requirePermission: 'inv.hotels.read',
      },
      {
        id: 'activities',
        to: '/activities',
        icon: Sparkles,
        requirePermission: 'inv.activities.read',
      },
      { id: 'transfers', to: '/transfers', icon: Car, requirePermission: 'inv.transfers.read' },
      { id: 'visas', to: '/visas', icon: Plane, requirePermission: 'inv.visas.read' },
      {
        id: 'insurance',
        to: '/insurance',
        icon: ShieldCheck,
        requirePermission: 'inv.insurance.read',
      },
      {
        id: 'meal-services',
        to: '/meal-services',
        icon: UtensilsCrossed,
        requirePermission: 'inv.meal_services.read',
      },
      { id: 'suppliers', to: '/suppliers', icon: Truck, requirePermission: 'inv.suppliers.read' },
      {
        id: 'airlines',
        to: '/airlines',
        icon: PlaneTakeoff,
        // Admin-only by default (see ADMIN_VISIBLE_ONLY_MODULES in the
        // permissions registry). Still in the /roles matrix, so an admin can
        // grant inv.airlines read/write to any role as needed.
        requirePermission: 'inv.airlines.read',
      },
      {
        id: 'destinations-master',
        to: '/destinations-master',
        icon: MapPin,
        requirePermission: 'inv.destinations.read',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Itinerary masters - typed lookups + pricing knobs.
  // ---------------------------------------------------------------------------
  {
    id: 'itinerary-masters',
    icon: Compass,
    items: [
      {
        id: 'meal-plans',
        to: '/meal-plans',
        icon: Utensils,
        requirePermission: 'itin.meal_plans.read',
      },
      {
        id: 'room-types',
        to: '/room-types',
        icon: BedDouble,
        requirePermission: 'itin.room_types.read',
      },
      {
        id: 'occupancy-types',
        to: '/occupancy-types',
        icon: Users,
        requirePermission: 'itin.occupancy_types.read',
      },
      {
        id: 'vehicle-types',
        to: '/vehicle-types',
        icon: Car,
        requirePermission: 'itin.vehicle_types.read',
      },
      {
        id: 'languages',
        to: '/languages',
        icon: LanguagesIcon,
        requirePermission: 'itin.languages.read',
      },
      { id: 'cuisines', to: '/cuisines', icon: Salad, requirePermission: 'itin.cuisines.read' },
      {
        id: 'markup-profiles',
        to: '/markup-profiles',
        icon: Percent,
        // 2026-07-09. DB-gated on `itin.markups.write` so admin can
        // manage visibility from the /roles matrix. Legacy `ops` role
        // holds `itin.markups.write` via group iteration and will now
        // see this item; admin can revoke from the matrix if needed.
        requirePermission: 'itin.markups.write',
      },
      {
        id: 'coupons',
        to: '/coupons',
        icon: Ticket,
        requirePermission: 'itin.coupons.read',
        hiddenFromNav: true,
      },
      {
        // 2026-10-07. Hidden from the sidebar (not in use) - route, page and
        // permission stay so nothing else breaks; only the nav link is dropped.
        id: 'discounts',
        to: '/discounts',
        icon: Percent,
        requirePermission: 'itin.discounts.read',
        hiddenFromNav: true,
      },
      {
        // 2026-10-07. "Upsell" - hidden from the sidebar (not in use); route,
        // page and permission retained, only the nav link removed.
        id: 'additional-discounts',
        to: '/additional-discounts',
        icon: Percent,
        requirePermission: 'itin.additional_discounts.read',
        hiddenFromNav: true,
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Lead & CRM masters - the lead-pipeline configuration.
  // ---------------------------------------------------------------------------
  {
    id: 'crm',
    icon: Users,
    items: [
      {
        id: 'query-types',
        to: '/query-types',
        icon: Tags,
        requirePermission: 'crm.query_types.read',
      },
      {
        id: 'query-sources',
        to: '/query-sources',
        icon: Share2,
        requirePermission: 'crm.query_sources.read',
      },
      {
        id: 'query-sub-sources',
        to: '/query-sub-sources',
        icon: GitBranch,
        requirePermission: 'crm.query_sub_sources.read',
      },
      {
        id: 'query-mediums',
        to: '/query-mediums',
        icon: MessageSquare,
        requirePermission: 'crm.query_mediums.read',
      },
      {
        id: 'query-statuses',
        to: '/query-statuses',
        icon: Flag,
        requirePermission: 'crm.query_statuses.read',
      },
      {
        id: 'query-sub-statuses',
        to: '/query-sub-statuses',
        icon: ListTree,
        requirePermission: 'crm.query_sub_statuses.read',
      },
      {
        id: 'call-statuses',
        to: '/call-statuses',
        icon: Phone,
        requirePermission: 'crm.call_statuses.read',
      },
      {
        id: 'call-sub-statuses',
        to: '/call-sub-statuses',
        icon: Phone,
        requirePermission: 'crm.call_sub_statuses.read',
      },
      {
        id: 'lost-reasons',
        to: '/lost-reasons',
        icon: ThumbsDown,
        requirePermission: 'crm.lost_reasons.read',
      },
      {
        // 2026-09-15. Cost heads for the Marketing report (the Spend rows of
        // the sheet). Gated on the report's WRITE code: it is set-up, not
        // reading, and the operator working the report never needs it.
        id: 'marketing-cost-heads',
        to: '/marketing-cost-heads',
        icon: Coins,
        requirePermission: 'ops.marketing_reports.write',
      },
      {
        id: 'trip-types',
        to: '/trip-types',
        icon: Plane,
        requirePermission: 'crm.trip_types.read',
      },
      {
        id: 'followup-reasons',
        to: '/followup-reasons',
        icon: Phone,
        requirePermission: 'crm.followup_reasons.read',
      },
      { id: 'branches', to: '/branches', icon: Building2, requirePermission: 'crm.branches.read' },
      {
        id: 'sub-branches',
        to: '/sub-branches',
        icon: GitBranch,
        // Reuses the Branch read permission - same audience curates both.
        requirePermission: 'crm.branches.read',
      },
      {
        id: 'departments',
        to: '/departments',
        icon: Briefcase,
        requirePermission: 'crm.departments.read',
      },
      {
        id: 'agent-targets',
        to: '/agent-targets',
        icon: Target,
        requirePermission: 'crm.agent_targets.read',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Global masters - geography + finance reference.
  // ---------------------------------------------------------------------------
  {
    id: 'global',
    icon: Globe,
    items: [
      { id: 'countries', to: '/countries', icon: Globe, requirePermission: 'geo.countries.read' },
      { id: 'states', to: '/states', icon: Landmark, requirePermission: 'geo.states.read' },
      { id: 'cities-cms', to: '/cities', icon: MapPin, requirePermission: 'geo.cities.read' },
      {
        id: 'currencies',
        to: '/currencies',
        icon: Coins,
        requirePermission: 'geo.currencies.read',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Website - public-site CMS content + website-only masters.
  // ---------------------------------------------------------------------------
  {
    id: 'website',
    icon: Palette,
    items: [
      { id: 'packages', to: '/packages', icon: Package, requirePermission: 'web.packages.read' },
      { id: 'blogs', to: '/blogs', icon: Rss, requirePermission: 'web.blogs.read' },
      {
        id: 'blog-categories',
        to: '/categories',
        icon: Tags,
        requirePermission: 'web.blog_categories.read',
      },
      {
        id: 'destination-pages',
        to: '/destination-pages',
        icon: MapPinned,
        requirePermission: 'web.destination_pages.read',
      },
      {
        id: 'category-pages',
        to: '/category-pages',
        icon: LayoutGrid,
        requirePermission: 'web.category_pages.read',
      },
      {
        id: 'tourism-boards',
        to: '/tourism-boards',
        icon: Landmark,
        requirePermission: 'web.tourism_boards.read',
      },
      {
        id: 'landing-pages',
        to: '/landing-pages',
        icon: Sparkles,
        requirePermission: 'web.landing_pages.read',
      },
      // 2026-08-03. The two destination SEO guides. Separate items (and
      // separate permission modules) because the two are written by different
      // people - granting one must not hand over the other.
      {
        id: 'best-time-to-visit',
        to: '/best-time-to-visit',
        icon: CalendarClock,
        requirePermission: 'web.best_time_pages.read',
      },
      {
        id: 'best-places-to-visit',
        to: '/best-places-to-visit',
        icon: Compass,
        requirePermission: 'web.best_places_pages.read',
      },
      { id: 'pages', to: '/pages', icon: FileText, requirePermission: 'web.pages.read' },
      // 2026-07-09. Career gate split from `web.pages.read` into its
      // own module (`web.career.read`) so it appears as an editable
      // row in the /roles permission matrix. The seed migration
      // mirrors current visibility (anyone holding `web.pages.*` is
      // auto-granted the matching `web.career.*` grant) so this flip
      // is behavior-preserving for existing roles.
      { id: 'career', to: '/career', icon: Briefcase, requirePermission: 'web.career.read' },
      // 2026-09-22. Editor for the internal Welcome Utazzer landing page
      // (content lives in app_config, not the website CMS). Gated by the
      // web.welcome module (2026-10-04) so edit access can be granted beyond
      // ADMIN from the /roles matrix; `read` shows the link, `write` saves.
      {
        id: 'welcome-page',
        to: '/welcome-page',
        icon: PartyPopper,
        requirePermission: 'web.welcome.read',
      },
      {
        id: 'theme-collections',
        to: '/marketing-tags',
        icon: Palette,
        requirePermission: 'web.themes.read',
      },
      {
        id: 'hero-inclusions',
        to: '/hero-inclusions',
        icon: ListChecks,
        requirePermission: 'web.hero_inclusions.read',
      },
      {
        id: 'inclusions-exclusions',
        to: '/inclusions-exclusions',
        icon: ClipboardList,
        requirePermission: 'web.inclusions_exclusions.read',
      },
      {
        id: 'navigation',
        to: '/navigation',
        icon: MenuIcon,
        requirePermission: 'web.navigation.read',
      },
      // 2026-07-09. DB-gated on `ops.customer_pdf.read`. Admin holds it by
      // default via `listAllPermissionCodes()`; grant to other roles from
      // the /roles matrix when needed. Router guard uses the same code.
      {
        id: 'customer-pdf',
        to: '/customer-pdf',
        icon: FileBadge,
        requirePermission: 'ops.customer_pdf.read',
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Finance - GST invoicing + money tools generated from bookings (admin +
  // finance). The read-only reports live in their own "Financial Reports"
  // section below.
  // ---------------------------------------------------------------------------
  {
    id: 'finance',
    icon: ReceiptIndianRupee,
    items: [
      {
        // GST tax invoices minted from bookings. Role-gated to ADMIN + FINANCE
        // (matches the invoicing API's @Roles guard); the route uses the same
        // RequireRole allow list as its second line of defence.
        id: 'invoices',
        to: '/invoices',
        icon: ReceiptIndianRupee,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Bank book reconciliation: verify / create receipts + supplier payments.
        id: 'bank-reconcile',
        to: '/reports/bank-reconcile',
        icon: Landmark,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Global credit-note listing raised against tax invoices for a period.
        id: 'credit-notes',
        to: '/reports/credit-notes',
        icon: FileMinus,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Invoice supplier-change approvals. ADMIN only (API + route guard);
        // canSeeNavItem evaluates ADMIN-only items as the real role on Services.
        id: 'supplier-change-approvals',
        to: '/invoices/supplier-change-approvals',
        icon: ShieldCheck,
        requireRole: ['ADMIN'],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Financial Reports - read-only finance/sales reports (the MYTAdmin rebuild).
  // Clubbed under one collapsible section so they do not crowd the Finance menu.
  // ---------------------------------------------------------------------------
  {
    id: 'financial-reports',
    icon: ChartColumn,
    items: [
      {
        // Booking detail sales register: one row per invoice with margin + due.
        id: 'sales-register',
        to: '/reports/sales-register',
        icon: ClipboardList,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Deposit booking: bookings created in-period that still owe a balance.
        id: 'deposit-booking',
        to: '/reports/deposit-booking',
        icon: FileText,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Client outstanding: invoices where the customer still owes money.
        id: 'client-outstanding',
        to: '/reports/client-outstanding',
        icon: Coins,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Cash-flow risk: supplier paid but the customer has paid nothing.
        id: 'supplier-paid-outstanding',
        to: '/reports/supplier-paid-outstanding',
        icon: FileMinus,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Balance due: deposit installment schedule grouped by due date.
        id: 'balance-due',
        to: '/reports/balance-due',
        icon: CalendarRange,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Supplier payment status (paid / unpaid) across invoices for a period.
        id: 'supplier-payments',
        to: '/reports/supplier-payments',
        icon: HandCoins,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Planned supplier installments aged into buckets (working capital).
        id: 'supplier-payment-schedule',
        to: '/reports/supplier-payment-schedule',
        icon: CalendarClock,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Cross-invoice ledger for one supplier with running totals.
        id: 'supplier-ledger',
        to: '/reports/supplier-ledger',
        icon: ScrollText,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Bank transaction statement: Dr/Cr passbook (receipts vs payments).
        id: 'bank-statement',
        to: '/reports/bank-statement',
        icon: Landmark,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Operation pending: future check-in bookings still partially owing.
        id: 'operation-pending',
        to: '/reports/operation-pending',
        icon: PlaneTakeoff,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Agent report card: monthly sales pivot per source/agent for one FY.
        id: 'agent-report-card',
        to: '/reports/agent-report-card',
        icon: ChartColumn,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // Monthly sale: per-user monthly sale + profit pivot (retail) for one FY.
        id: 'monthly-sale',
        to: '/reports/monthly-sale',
        icon: ChartColumn,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // GST output tax register (invoices + credit notes) for a period.
        id: 'gst-output-register',
        to: '/reports/gst-output-register',
        icon: FileSpreadsheet,
        requireRole: ['ADMIN', 'FINANCE'],
      },
      {
        // TCS register: invoices that collected TCS for a period.
        id: 'tcs-register',
        to: '/reports/tcs-register',
        icon: FileSpreadsheet,
        requireRole: ['ADMIN', 'FINANCE'],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // System - admin-only controls.
  // ---------------------------------------------------------------------------
  {
    id: 'system',
    icon: Settings,
    items: [
      { id: 'settings', to: '/settings', icon: Settings, requirePermission: 'sys.settings.read' },
      {
        id: 'notifications',
        to: '/notifications',
        icon: Bell,
        requirePermission: 'sys.notifications.read',
      },
      { id: 'secrets', to: '/secrets', icon: KeyRound, requirePermission: 'sys.secrets.read' },
      { id: 'meta-capi', to: '/meta-capi', icon: Share2, requirePermission: 'sys.meta_capi.read' },
      { id: 'users', to: '/users', icon: Users, requirePermission: 'sys.users.read' },
      { id: 'roles', to: '/roles', icon: ShieldCheck, requirePermission: 'sys.roles.read' },
      // Admin-only session/auth audit trail (login, logout, idle events + IP/geo).
      // Hidden from one specific admin (prod-only account) per request.
      {
        id: 'session-audit',
        to: '/session-audit',
        icon: Fingerprint,
        requireRole: ['ADMIN'],
        hideForEmails: ['shambhu@utazzoholidays.com'],
      },
      // Admin-only TBO API audit log (every flight/hotel call to TBO).
      {
        id: 'tbo-audit',
        to: '/tbo-audit',
        icon: ScrollText,
        requireRole: ['ADMIN'],
      },
      // Admin-only WhatsApp delivery log - agents see no send failures in the
      // chat widget, so this is the one place a failed message is answerable.
      {
        id: 'whatsapp-log',
        to: '/whatsapp-log',
        icon: MessageSquareWarning,
        requireRole: ['ADMIN'],
      },
    ],
  },
] as const;

/** Flat list of all nav items, in group order. */
export const NAV_ITEMS: readonly NavItemConfig[] = NAV_GROUPS.flatMap((g) => g.items);
